import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Upload, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

export const CategoryImporter = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Read CSV file
      const csvText = await file.text();

      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error('You must be logged in');
        return;
      }

      // Call the edge function
      const { data, error } = await supabase.functions.invoke('import-categories', {
        body: { csvData: csvText }
      });

      if (error) throw error;

      setResult(data);
      
      if (data.success) {
        toast.success(`Successfully imported ${data.summary.imported} categories!`);
      } else {
        toast.error('Import completed with errors');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to import categories');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Import Categories from CSV</CardTitle>
        <CardDescription>
          Upload the MinorSoccerTalks_simplified.csv file to import all categories into the database.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="flex-1"
            disabled={loading}
          />
          <Button
            onClick={handleImport}
            disabled={!file || loading}
            className="min-w-32"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Import
              </>
            )}
          </Button>
        </div>

        {file && !result && (
          <Alert>
            <AlertDescription>
              Ready to import from: <strong>{file.name}</strong>
            </AlertDescription>
          </Alert>
        )}

        {result && (
          <div className="space-y-2">
            <Alert variant={result.success ? "default" : "destructive"}>
              <AlertDescription>
                <div className="space-y-2">
                  <div className="font-semibold">Import Summary</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span>Imported: {result.summary.imported}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>Skipped: {result.summary.skipped}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-500" />
                      <span>Errors: {result.summary.errors}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>Total: {result.summary.total}</span>
                    </div>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
